// One trigger per filterable visible column, in configured order, then More filters for the triggers that do
// not fit the toolbar. Below 768px the triggers wrap instead and nothing folds. The bar owns the numeric and
// date drafts (drafts.js), so a draft outlives its panel and moves with its editor.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useTable } from '../context'
import { filterTypeOf, isFilterActive } from '../query'
import { DateFilter } from './DateFilter'
import { DraftsContext, triggerText, useDraftStore } from './drafts'
import { EnumFilter } from './EnumFilter'
import { FilterOpenContext, triggerClass } from './FilterMenu'
import { MORE_FILTERS, MoreFilters } from './MoreFilters'
import { NumericFilter } from './NumericFilter'
import { MORE_KEY, useFilterOverflow } from './useFilterOverflow'

const BY_TYPE = { numeric: NumericFilter, date: DateFilter }
const NONE = []

export const filterFor = column => BY_TYPE[filterTypeOf(column)] || EnumFilter

// The key of the filter whose panel is open (MORE_KEY for More filters). Each trigger's menu reports its
// open state in the same event that opens or closes it, so the bar never lays out against a stale panel.
const useOpenKey = () => {
  const [openKey, setOpenKey] = useState(null)
  const reportOpen = useCallback((key, open) => {
    setOpenKey(current => {
      if (open) return key
      return current === key ? null : current
    })
  }, [])
  return [openKey, reportOpen]
}

const triggersIn = group =>
  Array.from(group.children)
    .filter(child => child.dataset.filterKey)
    .map(child => ({
      key: child.dataset.filterKey,
      button: child.querySelector('[data-menu-trigger]'),
    }))

const focusLost = () => {
  const active = document.activeElement
  return !active || active === document.body || !active.isConnected
}

export const FilterBar = () => {
  const { state, layout, geometry } = useTable()
  const { filters } = state.query
  const { hidden } = state.view
  const columns = layout.filterColumns.filter(
    column => filterTypeOf(column) && !hidden.includes(column.key)
  )
  const keys = columns.map(column => column.key)
  const keySignature = keys.join('\n')
  const drafts = useDraftStore(key => state.query.filters[key])
  const { store } = drafts
  const groupRef = useRef(null)
  const measureRef = useRef(null)
  const lastFocus = useRef(null)
  const narrow = Boolean(layout.narrow)
  const present = keys.length > 0
  const [openKey, reportOpen] = useOpenKey()
  const moreOpen = openKey === MORE_KEY
  const active = keys.filter(key => isFilterActive(filters[key]))

  const folded = useFilterOverflow({
    groupRef,
    measureRef,
    keys,
    active,
    keep: moreOpen ? null : openKey,
    enabled: !narrow && present,
    freeWidth: (geometry && geometry.toolbarFreeWidth) || 0,
  })

  // an open More filters keeps the editors it opened with until it closes
  const [kept, setKept] = useState(NONE)
  useLayoutEffect(() => {
    setKept(moreOpen ? folded : NONE)
  }, [moreOpen])
  const listed = columns.filter(
    column => folded.includes(column.key) || kept.includes(column.key)
  )
  const showMore = !narrow && (folded.length > 0 || moreOpen)
  const foldedActive = folded.filter(key => active.includes(key)).length

  // clearing search and filters drops every draft; a column that leaves takes its draft with it
  useEffect(() => {
    if (state.reason === 'reset') store.dropAll()
  }, [state, store])
  useEffect(() => {
    store.retain(keySignature ? keySignature.split('\n') : NONE)
  }, [keySignature, store])

  // When closing a panel folds its trigger, focus moves to More filters; when More filters leaves (it closed
  // with nothing folded, or the layout went narrow), focus moves to the last visible filter trigger.
  const previous = useRef({ folded: NONE, more: false })
  useLayoutEffect(() => {
    const was = previous.current
    previous.current = { folded, more: showMore }
    const group = groupRef.current
    const key = lastFocus.current
    if (!group || !key || !focusLost()) return
    const triggers = triggersIn(group)
    let target = null
    if (key === MORE_KEY && was.more && !showMore) {
      const visible = triggers.filter(t => t.key !== MORE_KEY)
      target = visible.length ? visible[visible.length - 1].button : null
    } else if (
      key !== MORE_KEY &&
      folded.includes(key) &&
      !was.folded.includes(key)
    ) {
      const more = triggers.find(t => t.key === MORE_KEY)
      target = more ? more.button : null
    }
    if (target) {
      lastFocus.current = null
      target.focus()
    }
  })

  if (!present) return null

  const tracked = key => () => {
    lastFocus.current = key
  }

  return (
    <DraftsContext.Provider value={drafts}>
      <FilterOpenContext.Provider value={reportOpen}>
        <div
          ref={groupRef}
          className={`me-table-filters${narrow ? ' is-narrow' : ''}`}
        >
          {columns
            .filter(column => !folded.includes(column.key))
            .map(column => {
              const Filter = filterFor(column)
              return (
                <span
                  key={column.key}
                  className="me-table-filter"
                  data-filter-key={column.key}
                  onFocus={tracked(column.key)}
                >
                  <Filter column={column} />
                </span>
              )
            })}
          {showMore && (
            <span
              className="me-table-filter"
              data-filter-key={MORE_KEY}
              onFocus={tracked(MORE_KEY)}
            >
              <MoreFilters columns={listed} activeCount={foldedActive} />
            </span>
          )}
          {!narrow && (
            // widths for the overflow pass; the text is drawn from data-label so it adds no readable copy
            <div
              ref={measureRef}
              className="me-table-filter-measure"
              aria-hidden="true"
            >
              {columns.map(column => (
                <span
                  key={column.key}
                  data-measure={column.key}
                  data-label={triggerText(column, filters[column.key])}
                  className={triggerClass(active.includes(column.key))}
                >
                  <i className="fa-regular fa-chevron-down me-btn-caret" />
                </span>
              ))}
              <span
                data-measure-more=""
                data-label={MORE_FILTERS}
                className={triggerClass(false)}
              >
                <span
                  className="me-badge-tint me-badge-tint-brand me-table-filter-count"
                  data-label={String(columns.length)}
                />
              </span>
            </div>
          )}
        </div>
      </FilterOpenContext.Provider>
    </DraftsContext.Provider>
  )
}
